import { queryOptions, useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { AvailableModel } from "@/api/generated/AvailableModel"
import type { ModelPrice } from "@/api/generated/ModelPrice"
import type { ModelRow } from "@/api/generated/ModelRow"
import type { ModelView } from "@/api/generated/ModelView"
import type { Provider } from "@/api/generated/Provider"
import type { ProviderTest } from "@/api/generated/ProviderTest"
import { useApiMutation } from "@/api/mutation"

export const PROVIDER_KINDS = ["anthropic", "openai", "openai_compatible", "ollama"] as const
export type ProviderKind = (typeof PROVIDER_KINDS)[number]

export const providerKeys = {
  all: ["providers"] as const,
  list: () => [...providerKeys.all, "list"] as const,
  detail: (id: number) => [...providerKeys.all, "detail", id] as const,
  available: (id: number) => [...providerKeys.all, "available", id] as const,
}

export const modelKeys = {
  all: ["models"] as const,
  list: () => [...modelKeys.all, "list"] as const,
  detail: (id: number) => [...modelKeys.all, "detail", id] as const,
  prices: (id: number) => [...modelKeys.all, "prices", id] as const,
}

// ------------------------------------------------------------------ providers

export const providersQuery = () =>
  queryOptions({
    queryKey: providerKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<Provider>>("/providers", { signal }),
    select: (r) => r.items,
  })

export const useProviders = () => useQuery(providersQuery())

/** Models the provider's API reports (live call; may be slow or fail). */
export const useAvailableModels = (id: number | undefined) =>
  useQuery({
    queryKey: providerKeys.available(id ?? 0),
    queryFn: ({ signal }) =>
      api.get<ListResponse<AvailableModel>>(`/providers/${id}/available-models`, { signal }),
    select: (r) => r.items,
    enabled: id != null,
    staleTime: 0,
    retry: false,
  })

export interface ProviderInput {
  name?: string
  kind?: ProviderKind
  base_url?: string
  /** Omit to keep the stored key, "" to clear it. */
  api_key?: string
  /** Replaces all custom headers when present. */
  headers?: Record<string, string>
  strict_compat?: boolean
}

export function useCreateProvider() {
  const { t } = useTranslation()
  return useApiMutation<Provider, ProviderInput>({
    mutationFn: (body) => api.post("/providers", body),
    invalidates: [providerKeys.all],
    successToast: (p) => t("studio.providers.created", { name: p.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateProvider() {
  const { t } = useTranslation()
  return useApiMutation<Provider, { id: number } & ProviderInput>({
    mutationFn: ({ id, ...body }) => api.patch(`/providers/${id}`, body),
    invalidates: [providerKeys.all, modelKeys.all],
    successToast: (p) => t("studio.providers.updated", { name: p.name }),
    errorToast: "unlessFields",
  })
}

export function useDeleteProvider() {
  const { t } = useTranslation()
  return useApiMutation<void, Provider>({
    mutationFn: (p) => api.delete(`/providers/${p.id}`),
    invalidates: [providerKeys.all],
    successToast: (_d, p) => t("studio.providers.deleted", { name: p.name }),
  })
}

export function useTestProvider() {
  return useApiMutation<ProviderTest, number>({
    mutationFn: (id) => api.post(`/providers/${id}/test`),
  })
}

export interface ImportModelInput {
  model: string
  name?: string
  context_length?: number | null
}

export function useImportModels(providerId: number) {
  const { t } = useTranslation()
  return useApiMutation<ListResponse<ModelRow>, ImportModelInput[]>({
    mutationFn: (models) => api.post(`/providers/${providerId}/import-models`, { models }),
    invalidates: [providerKeys.all, modelKeys.all],
    successToast: (r) => t("studio.providers.import.done", { count: r.items.length }),
  })
}

// ------------------------------------------------------------------ models

export const modelsQuery = () =>
  queryOptions({
    queryKey: modelKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<ModelView>>("/models", { signal }),
    select: (r) => r.items,
  })

export const useModels = () => useQuery(modelsQuery())

export interface ModelInput {
  provider_id?: number
  model?: string
  name?: string
  description?: string
  fallback_model_id?: number | null
  context_length?: number | null
  max_output_tokens?: number | null
  supports_vision?: boolean
  supports_tools?: boolean
}

export function useCreateModel() {
  const { t } = useTranslation()
  return useApiMutation<ModelView, ModelInput>({
    mutationFn: (body) => api.post("/models", body),
    invalidates: [modelKeys.all, providerKeys.all],
    successToast: (m) => t("studio.models.created", { name: m.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateModel() {
  const { t } = useTranslation()
  return useApiMutation<ModelView, { id: number } & ModelInput>({
    mutationFn: ({ id, ...body }) => api.patch(`/models/${id}`, body),
    invalidates: [modelKeys.all, providerKeys.all],
    successToast: (m) => t("studio.models.updated", { name: m.name }),
    errorToast: "unlessFields",
  })
}

export function useDeleteModel() {
  const { t } = useTranslation()
  return useApiMutation<void, ModelView>({
    mutationFn: (m) => api.delete(`/models/${m.id}`),
    invalidates: [modelKeys.all, providerKeys.all],
    successToast: (_d, m) => t("studio.models.deleted", { name: m.name }),
  })
}

// ------------------------------------------------------------------ prices

export const useModelPrices = (modelId: number | undefined) =>
  useQuery({
    queryKey: modelKeys.prices(modelId ?? 0),
    queryFn: ({ signal }) => api.get<ListResponse<ModelPrice>>(`/models/${modelId}/prices`, { signal }),
    select: (r) => r.items,
    enabled: modelId != null,
  })

export interface PriceInput {
  input_per_1m?: number
  output_per_1m?: number
  cache_read_per_1m?: number | null
  cache_write_per_1m?: number | null
  currency?: string
  /** YYYY-MM-DD */
  effective_from?: string
}

export function useCreatePrice(modelId: number) {
  const { t } = useTranslation()
  return useApiMutation<ModelPrice, PriceInput>({
    mutationFn: (body) => api.post(`/models/${modelId}/prices`, body),
    invalidates: [modelKeys.all],
    successToast: () => t("studio.models.prices.created"),
    errorToast: "unlessFields",
  })
}

export function useUpdatePrice(modelId: number) {
  const { t } = useTranslation()
  return useApiMutation<ModelPrice, { id: number } & PriceInput>({
    mutationFn: ({ id, ...body }) => api.patch(`/models/${modelId}/prices/${id}`, body),
    invalidates: [modelKeys.all],
    successToast: () => t("studio.models.prices.updated"),
    errorToast: "unlessFields",
  })
}

export function useDeletePrice(modelId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, ModelPrice>({
    mutationFn: (p) => api.delete(`/models/${modelId}/prices/${p.id}`),
    invalidates: [modelKeys.all],
    successToast: () => t("studio.models.prices.deleted"),
  })
}
